import { Component } from '@theme/component';

/**
 * Numbered list of links that becomes tabs: each link points to a panel by id.
 * Without JavaScript the links jump to panels shown one after another; once
 * connected, only the selected panel shows, arrow keys move between tabs, the
 * address hash opens a panel, and selecting a block in the theme editor shows it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - Wrapper of the tabs
 * @property {HTMLAnchorElement[]} tabs - Links, one per panel
 * @property {HTMLElement[]} panels - Panels, in the same order as the tabs
 *
 * @extends {Component<Refs>}
 */
class FifiTabsComponent extends Component {
  requiredRefs = ['list'];

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Shows the panel at `index`.
   * @param {number} index
   * @param {boolean} [focus]
   */
  select(index, focus = false) {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (panels[i]) panels[i].hidden = !selected;
    }

    if (focus) tabs[index]?.focus();
  }

  /** @param {Event} event */
  handleClick(event) {
    const index = this.#indexOf(event.target);
    if (index < 0) return;

    event.preventDefault();
    this.select(index);
    const panel = this.refs.panels?.[index];
    if (panel) window.history.replaceState(null, '', `#${panel.id}`);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const index = this.#indexOf(event.target);
    if (index < 0) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    /** @type {Record<string, number>} */
    const steps = { ArrowDown: 1, ArrowUp: -1, ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 };
    let next = null;

    if (event.key in steps) next = (index + steps[event.key] + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    this.select(next, true);
  }

  #setup() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    if (tabs.length < 2 || tabs.length !== panels.length) return;

    this.refs.list.setAttribute('role', 'tablist');
    this.refs.list.setAttribute('aria-orientation', 'vertical');
    for (const [i, tab] of tabs.entries()) {
      const panel = panels[i];
      if (!panel) continue;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;
    }
    this.toggleAttribute('data-enhanced', true);

    window.addEventListener(
      'hashchange',
      () => {
        const index = this.#hashIndex();
        if (index >= 0) this.select(index);
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const index = panels.findIndex((panel) => event.target instanceof Node && panel.contains(event.target));
        if (index >= 0) this.select(index);
      },
      { signal }
    );

    const initial = this.#hashIndex();
    this.select(initial >= 0 ? initial : 0);
  }

  /** @param {EventTarget | null} target */
  #indexOf(target) {
    if (!(target instanceof Element)) return -1;
    const tab = target.closest('[role="tab"]');
    return tab instanceof HTMLAnchorElement ? (this.refs.tabs ?? []).indexOf(tab) : -1;
  }

  #hashIndex() {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return -1;
    return (this.refs.panels ?? []).findIndex((panel) => panel.id === id);
  }
}

if (!customElements.get('fifi-tabs-component')) {
  customElements.define('fifi-tabs-component', FifiTabsComponent);
}
